import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { createProperty } from '../../services/selfServiceService';

const PropertyCreationWizard = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation(['user', 'common']);
  const { instance } = useMsal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    propertyName: '',
    propertyDescription: '',
    city: '',
    country: '',
    businessEmail: '',
    businessPhone: '',
    streetAddress: '',
    businessType: ''
  });

  const [errors, setErrors] = useState({});

  const steps = [
    t('propertyCreation.step1Title'),
    t('propertyCreation.step2Title')
  ];

  const businessTypes = [
    'Hotel',
    'Bed & Breakfast',
    'Vacation Rental',
    'Hostel',
    'Resort',
    'Apartment',
    'Guesthouse',
    'Villa',
    'Other'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.propertyName.trim()) {
      newErrors.propertyName = t('propertyCreation.validation.propertyNameRequired');
    }
    if (!formData.city.trim()) {
      newErrors.city = t('propertyCreation.validation.cityRequired');
    }
    if (!formData.country.trim()) {
      newErrors.country = t('propertyCreation.validation.countryRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.businessEmail.trim()) {
      newErrors.businessEmail = t('propertyCreation.validation.businessEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)) {
      newErrors.businessEmail = t('propertyCreation.validation.businessEmailInvalid');
    }
    if (!formData.businessPhone.trim()) {
      newErrors.businessPhone = t('propertyCreation.validation.businessPhoneRequired');
    }
    if (!formData.streetAddress.trim()) {
      newErrors.streetAddress = t('propertyCreation.validation.streetAddressRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    } else if (activeStep === 1 && validateStep2()) {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
  const result = await createProperty(formData, instance);
  // Pass full result upward so caller can set context & navigate
  onSuccess(result);
  // Close the wizard after notifying parent
  handleClose();
    } catch (err) {
      setError(err.response?.data || t('propertyCreation.error.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      propertyName: '',
      propertyDescription: '',
      city: '',
      country: '',
      businessEmail: '',
      businessPhone: '',
      streetAddress: '',
      businessType: ''
    });
    setErrors({});
    setError('');
    onClose();
  };

  const renderStep1 = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('propertyCreation.step1Title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('propertyCreation.step1Description')}
      </Typography>
      
      <TextField
        fullWidth
        label={t('propertyCreation.propertyName')}
        value={formData.propertyName}
        onChange={(e) => handleInputChange('propertyName', e.target.value)}
        error={!!errors.propertyName}
        helperText={errors.propertyName}
        sx={{ mb: 2 }}
        required
      />
      
      <TextField
        fullWidth
        label={t('propertyCreation.propertyDescription')}
        value={formData.propertyDescription}
        onChange={(e) => handleInputChange('propertyDescription', e.target.value)}
        multiline
        rows={3}
        sx={{ mb: 2 }}
        placeholder={t('propertyCreation.propertyDescriptionPlaceholder')}
      />
      
      <TextField
        fullWidth
        label={t('propertyCreation.city')}
        value={formData.city}
        onChange={(e) => handleInputChange('city', e.target.value)}
        error={!!errors.city}
        helperText={errors.city}
        sx={{ mb: 2 }}
        required
      />
      
      <TextField
        fullWidth
        label={t('propertyCreation.country')}
        value={formData.country}
        onChange={(e) => handleInputChange('country', e.target.value)}
        error={!!errors.country}
        helperText={errors.country}
        required
      />
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('propertyCreation.step2Title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('propertyCreation.step2Description')}
      </Typography>
      
      <TextField
        fullWidth
        label={t('propertyCreation.businessEmail')}
        type="email"
        value={formData.businessEmail}
        onChange={(e) => handleInputChange('businessEmail', e.target.value)}
        error={!!errors.businessEmail}
        helperText={errors.businessEmail}
        sx={{ mb: 2 }}
        required
      />
      
      <TextField
        fullWidth
        label={t('propertyCreation.businessPhone')}
        value={formData.businessPhone}
        onChange={(e) => handleInputChange('businessPhone', e.target.value)}
        error={!!errors.businessPhone}
        helperText={errors.businessPhone}
        sx={{ mb: 2 }}
        required
      />
      
      <TextField
        fullWidth
        label={t('propertyCreation.streetAddress')}
        value={formData.streetAddress}
        onChange={(e) => handleInputChange('streetAddress', e.target.value)}
        error={!!errors.streetAddress}
        helperText={errors.streetAddress}
        sx={{ mb: 2 }}
        required
      />
      
      <TextField
        fullWidth
        select
        label={t('propertyCreation.businessType')}
        value={formData.businessType}
        onChange={(e) => handleInputChange('businessType', e.target.value)}
        sx={{ mb: 2 }}
      >
        {businessTypes.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        {t('properties.createProperty')}
      </DialogTitle>
      
      <DialogContent>
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ 
            mb: { xs: 2.5, sm: 3 },
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              mt: { xs: 0.5, sm: 0 }
            },
            '& .MuiStepIcon-root': {
              fontSize: { xs: '1.5rem', sm: '1.5rem' }
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel 
                sx={{
                  '& .MuiStepLabel-labelContainer': {
                    maxWidth: { xs: '200px', sm: 'none' }
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 ? renderStep1() : renderStep2()}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('buttons.cancel', { ns: 'common' })}
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            {t('buttons.back', { ns: 'common' })}
          </Button>
        )}
        
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {activeStep === 0 ? t('buttons.next', { ns: 'common' }) : t('propertyCreation.createProperty')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyCreationWizard;
